import { useCallback, useMemo, useState } from 'react'

import type { FieldError } from '../screens/FormErrors.js'

/**
 * Inline validation on blur, and the summary that goes with it. `DESIGN-TOKENS.md` §16.5, §17.
 *
 * > "**Inline validation on blur, never on keystroke** · form error summary at the top listing
 * > every failed field as a link"
 *
 * and §17's Error row: "`--lm-danger` border, message below, icon. **Cleared on edit**".
 *
 * **Neither half was in the counter.** There were two `onBlur` handlers in the whole application
 * and neither validated anything, and `FormErrors` was built and used by exactly one screen. Every
 * other form answers on submit, as one sentence, usually under the button.
 *
 * **And answering on submit is not the smaller version of this, it is a different thing.**
 * `NewCustomer` returned at the first failure, so a form with a missing number and a date of birth
 * in 2062 reported the number, and the person fixed it, pressed the button again and found out
 * about the year. Two round trips through a form to learn two things that were both knowable when
 * they were typed.
 *
 * **Blur, not keystroke, and the difference is the point.** A required-field message that appears
 * on the first character says "a customer needs a name" to somebody in the middle of typing one.
 * So a field's message appears when the person leaves it, is cleared the moment they edit it
 * again, and submit shows every field at once.
 *
 * The checks are plain functions over the current state rather than a schema, because they are
 * already written: each one is a sentence from the i18n catalogue and a condition the service
 * refuses on, and a refused write blocks the whole queue (`OFF-001a`). That is what these are for.
 */

export interface FieldCheck {
  /** The input's `id`, so the summary's link can reach it and `aria-describedby` can point at it. */
  readonly fieldId: string
  /** What the field is called, as the summary link's text. */
  readonly label: string
  /** The message when the value is wrong, or nothing when it is fine. */
  readonly failure: string | undefined
}

export interface FieldChecks {
  /** Every failure worth showing, for `FormErrors`. Empty until a field is left or submit is tried. */
  readonly summary: readonly FieldError[]
  /** The message to put under one field, or nothing. */
  readonly messageFor: (fieldId: string) => string | undefined
  /** `onBlur` for an input: shows that field's message, if it has one. */
  readonly leave: (fieldId: string) => void
  /** `onChange` for an input: §17's "cleared on edit". */
  readonly edit: (fieldId: string) => void
  /**
   * Call from the submit handler. Returns true when there is nothing wrong.
   *
   * On false it shows every failure at once, which is the one moment a person has asked about the
   * whole form rather than about the field they are in.
   */
  readonly ready: () => boolean
  /** Spread onto the input. Omits the attributes rather than setting them undefined. */
  readonly inputProps: (fieldId: string) => {
    readonly onBlur: () => void
    readonly 'aria-invalid'?: true
    readonly 'aria-describedby'?: string
  }
}

/** The id of the element holding one field's message. One convention, so nothing has to pass it. */
export const errorId = (fieldId: string): string => `${fieldId}-error`

export function useFieldChecks(checks: readonly FieldCheck[]): FieldChecks {
  /**
   * The fields whose message may be shown: the ones a person has finished with.
   *
   * A set rather than a flag per field, because the question is never "is this field valid" (the
   * check answers that on every render) but "has this person seen this field yet". A message about
   * a field somebody has not reached is a form shouting at them before they start.
   */
  const [left, setLeft] = useState<readonly string[]>([])
  const [asked, setAsked] = useState(false)

  const failureOf = useCallback(
    (fieldId: string): string | undefined =>
      checks.find((check) => check.fieldId === fieldId)?.failure,
    [checks],
  )

  const shown = useCallback(
    (fieldId: string): boolean => asked || left.includes(fieldId),
    [asked, left],
  )

  const messageFor = useCallback(
    (fieldId: string): string | undefined =>
      shown(fieldId) ? failureOf(fieldId) : undefined,
    [shown, failureOf],
  )

  const summary = useMemo<readonly FieldError[]>(
    () =>
      checks
        .filter((check) => check.failure !== undefined && (asked || left.includes(check.fieldId)))
        .map((check) => ({
          fieldId: check.fieldId,
          label: check.label,
          message: check.failure as string,
        })),
    [checks, asked, left],
  )

  const leave = useCallback((fieldId: string): void => {
    setLeft((was) => (was.includes(fieldId) ? was : [...was, fieldId]))
  }, [])

  const edit = useCallback(
    (fieldId: string): void => {
      /**
       * §17: "Cleared on edit", and only this field's.
       *
       * **`asked` cannot simply be turned off here**, which is what the first version did. A
       * submit shows every failure; editing the name then hid the message about the number as
       * well, and the number was something the person had asked about and not touched. So the
       * submit's effect is kept by naming the other fields explicitly, and only the edited one
       * goes quiet.
       *
       * Found by a mutation that deleted this whole body and killed no test: the case meant to
       * cover it typed a valid name, so the check stopped failing and the message would have gone
       * either way.
       */
      const others = checks.map((check) => check.fieldId).filter((id) => id !== fieldId)
      setLeft((was) => {
        const keep = was.filter((id) => id !== fieldId)
        return asked ? [...new Set([...keep, ...others])] : keep
      })
      setAsked(false)
    },
    [asked, checks],
  )

  const ready = useCallback((): boolean => {
    const clean = checks.every((check) => check.failure === undefined)
    if (!clean) setAsked(true)
    return clean
  }, [checks])

  const inputProps = useCallback(
    (fieldId: string) => {
      const message = messageFor(fieldId)
      return {
        onBlur: () => {
          leave(fieldId)
        },
        // `exactOptionalPropertyTypes`: an `aria-invalid={undefined}` is not the same as no
        // attribute, and `aria-describedby` pointing at an element that is not rendered is a
        // reference a screen reader follows to nothing.
        ...(message === undefined ? {} : { 'aria-invalid': true as const }),
        ...(message === undefined ? {} : { 'aria-describedby': errorId(fieldId) }),
      }
    },
    [messageFor, leave],
  )

  return { summary, messageFor, leave, edit, ready, inputProps }
}
